/* Couriers & Accounts — the migration view. Which couriers are actually live, how
   each is performing, and what's left to move off Clickpost.

   Hierarchy: rows are grouped by parent courier (courier_parent), built from the
   accounts list (the ground-truth account catalog — the same data backing the
   filter dropdowns) rather than from /health/adoption's own courier rows, which
   turned out to double-count a courier's accounts once per direction (see the
   Migration Runway comment below). Expanding a parent reveals its sub-couriers
   (courierCode + serviceType, e.g. "Delhivery · FORWARD" vs "Delhivery · RVP") —
   this no longer sets the page-wide courier filter; that filter now lives only
   in the Courier dropdown above.

   Direction: Forward and Reverse are separate tabs, and the choice scopes the whole tab —
   KPIs, scorecard and runway — not just the table. A brand runs different accounts, often at
   different migration stages, per direction, so one merged row per parent was averaging two
   independent stories. The filter rail's Delivery Type overrides the tab when set.

   Volume/failure/latency can't be grouped by courier AND direction in one call —
   /health/breakdown's COURIER dimension merges both directions into one number
   per courier code. So the numbers come from a direction-scoped fetch
   (deliveryType=FORWARD / REVERSE) joined back in by courier code; only the
   visible direction is fetched. */
import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Tooltip, Typography} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {LoadErrorPanel, TableSkeleton} from '../../components'
import {
  usePcisHealthAdoptionQuery,
  usePcisHealthBreakdownQuery,
  usePcisHealthLatencyQuery,
  usePcisNativeVolumeQuery,
  useProshipOrderCountQuery,
} from '../../../../redux/supplyApiSlice'
import {
  AMBER,
  CORAL,
  Empty,
  INDIGO,
  LabelledBar,
  Meter,
  MiniTable,
  Panel,
  Pill,
  SLATE,
  Stat,
  StatGrid,
  TEAL,
  VIOLET,
  ms,
  nf,
  pct,
  rateBand,
} from './parts'

/* RVP accounts are Reverse Pickup — the account catalog's serviceType for the
   API's deliveryType filter (FORWARD/REVERSE). */
const dirKey = (serviceType) => (serviceType === 'RVP' || serviceType === 'REVERSE' ? 'REVERSE' : 'FORWARD')

/* courier_parent isn't consistent across a brand's own accounts — e.g. Delhivery's
   forward accounts carry courier_parent "Delhivery" while its reverse accounts
   carry "Delhivery_Return". Strip a trailing direction suffix before grouping so
   both land under one parent; the un-suffixed spelling (if one exists in the
   data) wins as the display name. */
const stripDirectionSuffix = (name) => (name || '').replace(/[\s_-]+(return|reverse|rvp)$/i, '').trim()

/* A dashed-underline label with a tooltip — used on column headers below to flag ones that
   look comparable but aren't (Booked shipment vs Total trials/Share come from two different
   sources). */
function HeaderTip({label, title}) {
  return (
    <Tooltip title={title}>
      <Box component='span' sx={{borderBottom: '1px dashed', borderColor: 'divider'}}>
        {label}
      </Box>
    </Tooltip>
  )
}
HeaderTip.propTypes = {label: PropTypes.node, title: PropTypes.node}

function LegendItem({color, label}) {
  return (
    <Stack direction='row' alignItems='center' spacing={0.75} sx={{mb: 0.5}}>
      <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0}} />
      <Typography sx={{fontSize: 11.5}}>{label}</Typography>
    </Stack>
  )
}
LegendItem.propTypes = {color: PropTypes.string, label: PropTypes.node}

/* What the Failure and Polling pill colours mean — placed in the Courier scorecard's header
   as a hover target rather than spelled out inline, since it applies to every row at once. */
function ColorLegend() {
  return (
    <Tooltip
      arrow
      placement='bottom-end'
      title={
        <Box sx={{py: 0.5}}>
          <Typography sx={{fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', mb: 0.75}}>
            Failure rate
          </Typography>
          <LegendItem color={TEAL.main} label='Healthy — under 5%' />
          <LegendItem color={AMBER.main} label='Degraded — 5% to 20%' />
          <LegendItem color={CORAL.main} label='Critical — 20% and above' />
          <Typography sx={{fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', mt: 1.25, mb: 0.75}}>
            Polling
          </Typography>
          <LegendItem color={TEAL.main} label='Every native account is polling' />
          <LegendItem color={AMBER.main} label='Some native accounts have polling off' />
          <LegendItem color={CORAL.main} label='No native accounts are polling' />
        </Box>
      }
    >
      <Stack direction='row' alignItems='center' spacing={0.5} sx={{cursor: 'help', color: 'text.secondary'}}>
        <InfoOutlinedIcon sx={{fontSize: 15}} />
        <Typography sx={{fontSize: 11, fontWeight: 600}}>Colors</Typography>
      </Stack>
    </Tooltip>
  )
}

const DIR_LABEL = {FORWARD: 'Forward', REVERSE: 'Reverse'}

/* Segmented Forward/Reverse control. Rendered disabled rather than hidden when the filter rail
   has already pinned a direction, so the tab doesn't silently vanish — the user can see the
   choice exists and that something else is currently making it. */
function DirectionTabs({value, onChange, locked}) {
  return (
    <Tooltip title={locked ? 'Direction is set by the Delivery Type filter above — clear it to switch here' : ''}>
      <Stack
        direction='row'
        sx={{
          p: '2px',
          borderRadius: '9px',
          bgcolor: 'action.hover',
          opacity: locked ? 0.55 : 1,
          cursor: locked ? 'not-allowed' : 'default',
        }}
      >
        {['FORWARD', 'REVERSE'].map((d) => {
          const active = value === d
          return (
            <Box
              key={d}
              component='button'
              type='button'
              disabled={locked}
              onClick={() => onChange(d)}
              sx={{
                border: 0,
                px: 1.5,
                py: 0.5,
                borderRadius: '7px',
                fontSize: 11.5,
                fontWeight: 700,
                fontFamily: 'inherit',
                cursor: locked ? 'not-allowed' : 'pointer',
                color: active ? 'text.primary' : 'text.secondary',
                bgcolor: active ? 'background.paper' : 'transparent',
                boxShadow: active ? '0 1px 2px rgba(15,23,42,.10)' : 'none',
              }}
            >
              {DIR_LABEL[d]}
            </Box>
          )
        })}
      </Stack>
    </Tooltip>
  )
}
DirectionTabs.propTypes = {value: PropTypes.string, onChange: PropTypes.func, locked: PropTypes.bool}

export default function TabCouriers({filters, pollMs, accounts: allAccounts}) {
  const poll = pollMs ? {pollingInterval: pollMs} : undefined

  /* Forward / Reverse are separate tabs rather than one merged table: a brand runs different
     accounts, and often a different integration state, in each direction, so a single row per
     parent was averaging two independent migration stories into one.

     The direction scopes the WHOLE tab, not just the scorecard — leaving the account and polling
     KPIs fleet-wide above a direction-scoped table is exactly the card-disagrees-with-table
     mismatch we spent today removing. */
  const railDirection = filters.deliveryType === 'FORWARD' || filters.deliveryType === 'REVERSE' ? filters.deliveryType : null
  const [dirTab, setDirTab] = useState('FORWARD')
  /* The filter rail wins when it names a direction — otherwise the tab would claim to show
     Reverse while the rail had already narrowed every query to Forward. */
  const direction = railDirection || dirTab

  const accounts = useMemo(
    () => (allAccounts || []).filter((a) => dirKey(a.service_type || 'FORWARD') === direction),
    [allAccounts, direction]
  )

  /* Kept only for the (as-yet unreworked — see doc item 2.8) "Configured vs
     actual" panel below, and as the tab's loading/error gate. */
  const {
    data: adoption,
    isFetching: adoptionFetching,
    isError,
    error,
    refetch,
  } = usePcisHealthAdoptionQuery(filters, poll)

  /* Native (DI) order counts as PCIS recorded them at booking time, plus Proship's own total for
     the window. adoption.nativeSharePct above reflects each account's CURRENT config — switching an
     account today changes what adoption reports for orders already placed; these don't.

     Only the NATIVE side is available per courier: PCIS doesn't store Clickpost orders, so the
     fleet-wide Clickpost figure is derived (total − native) and can't be attributed per courier
     without grouping Proship's whole orders collection by courier (~190s for a 30-day window). */
  /* currentData, not data — see the note in TabOverview: `data` serves the previous window's
     value during a refetch, so pairing two independently-resolving queries can mix windows. */
  const {currentData: pcisNative} = usePcisNativeVolumeQuery({from: filters.from, to: filters.to}, poll)
  const {currentData: proshipCount} = useProshipOrderCountQuery({from: filters.from, to: filters.to}, poll)

  const nativeByParent = useMemo(() => {
    const m = new Map()
    ;(pcisNative?.parents || []).forEach((p) =>
      m.set(String(p.parent || '').toLowerCase(), Number(p.nativeOrders) || 0)
    )
    return m
  }, [pcisNative])

  const fleetRouting = useMemo(() => {
    const nativeOrders = Number(pcisNative?.nativeOrders) || 0
    const totalOrders = Number(proshipCount?.count) || 0
    if (!totalOrders) return null
    const nativeSharePct = (nativeOrders / totalOrders) * 100
    return {
      totalOrders,
      nativeOrders,
      nativeSharePct,
      clickpostOrders: Math.max(0, totalOrders - nativeOrders),
      clickpostSharePct: 100 - nativeSharePct,
    }
  }, [pcisNative, proshipCount])

  /* Only the visible direction is fetched now — the tab shows one at a time, so pulling both
     legs of the breakdown and latency queries was two wasted calls per render. */
  const wantsForward = direction === 'FORWARD'
  const wantsReverse = direction === 'REVERSE'

  const {data: byCourierFwd} = usePcisHealthBreakdownQuery(
    {...filters, dimension: 'COURIER', limit: 100, deliveryType: 'FORWARD'},
    {...poll, skip: !wantsForward}
  )
  const {data: byCourierRev} = usePcisHealthBreakdownQuery(
    {...filters, dimension: 'COURIER', limit: 100, deliveryType: 'REVERSE'},
    {...poll, skip: !wantsReverse}
  )
  const {data: latFwd} = usePcisHealthLatencyQuery({...filters, limit: 100, deliveryType: 'FORWARD'}, {...poll, skip: !wantsForward})
  const {data: latRev} = usePcisHealthLatencyQuery({...filters, limit: 100, deliveryType: 'REVERSE'}, {...poll, skip: !wantsReverse})

  const volMap = useMemo(() => {
    const m = new Map()
    ;(byCourierFwd?.rows || []).forEach((r) => m.set(`${r.key}::FORWARD`, r))
    ;(byCourierRev?.rows || []).forEach((r) => m.set(`${r.key}::REVERSE`, r))
    return m
  }, [byCourierFwd, byCourierRev])

  const latMap = useMemo(() => {
    const m = new Map()
    ;(latFwd?.rows || []).forEach((r) => m.set(`${r.key}::FORWARD`, r))
    ;(latRev?.rows || []).forEach((r) => m.set(`${r.key}::REVERSE`, r))
    return m
  }, [latFwd, latRev])

  /* Parent → sub-courier tree, built entirely from the accounts catalog. Grouped
     by a normalized parent key (see stripDirectionSuffix above) rather than the
     raw courier_parent string, since a brand's own accounts don't spell it the
     same way across directions. */
  const parents = useMemo(() => {
    const byParent = new Map() // normKey -> {displayName, couriers: Map}
    ;(accounts || []).forEach((a) => {
      const raw = a.courier_parent || a.courier_name || a.courier_code || 'Unknown'
      const stripped = stripDirectionSuffix(raw) || raw
      const normKey = stripped.toLowerCase()
      if (!byParent.has(normKey)) byParent.set(normKey, {displayName: stripped, couriers: new Map()})
      const bucket = byParent.get(normKey)
      if (raw.toLowerCase() === normKey) bucket.displayName = raw // un-suffixed spelling wins when we see it
      const serviceType = a.service_type || 'FORWARD'
      /* Keyed on the RAW (pre-strip) parent too — Delhivery's forward accounts and
         its Delhivery_Return accounts share both courier_code AND serviceType
         ("DELHIVERY"/"FORWARD"), so without this they'd merge into one sub-row
         instead of staying distinct under their shared parent. */
      const courierKey = `${raw}::${a.courier_code}::${serviceType}`
      if (!bucket.couriers.has(courierKey)) {
        bucket.couriers.set(courierKey, {courierCode: a.courier_code, courierName: a.courier_name, serviceType, accounts: []})
      }
      bucket.couriers.get(courierKey).accounts.push(a)
    })

    return Array.from(byParent.values(), ({displayName, couriers}) => {
      const name = displayName
      const courierList = Array.from(couriers.values())
        .map((c) => {
          const direction = dirKey(c.serviceType)
          const vol = volMap.get(`${c.courierCode}::${direction}`)
          const lat = latMap.get(`${c.courierCode}::${direction}`)
          const totalAccounts = c.accounts.length
          const nativeAccts = c.accounts.filter((a) => a.integration_type === 'NATIVE')
          const nativeAccounts = nativeAccts.length
          const pollingEnabledCount = nativeAccts.filter((a) => a.polling_enabled).length
          return {
            courierCode: c.courierCode,
            courierName: c.courierName,
            serviceType: c.serviceType,
            totalAccounts,
            nativeAccounts,
            clickpostAccounts: totalAccounts - nativeAccounts,
            pollingOffCount: nativeAccounts - pollingEnabledCount,
            total: vol?.total || 0,
            failed: vol?.failed || 0,
            p50Ms: lat?.p50Ms ?? null,
            p95Ms: lat?.p95Ms ?? null,
            live: nativeAccounts > 0,
          }
        })
        .sort((a, b) => b.total - a.total)
      return {name, couriers: courierList}
    }).sort((a, b) => {
      const at = a.couriers.reduce((s, c) => s + c.total, 0)
      const bt = b.couriers.reduce((s, c) => s + c.total, 0)
      return bt - at
    })
  }, [accounts, volMap, latMap])

  const grandTotal = useMemo(() => parents.reduce((s, p) => s + p.couriers.reduce((s2, c) => s2 + c.total, 0), 0), [parents])

  const [expanded, setExpanded] = useState(() => new Set())
  const toggle = (name) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })

  const displayRows = useMemo(() => {
    const out = []
    parents.forEach((p) => {
      const totalAccounts = p.couriers.reduce((s, c) => s + c.totalAccounts, 0)
      const nativeAccounts = p.couriers.reduce((s, c) => s + c.nativeAccounts, 0)
      const pollingOffCount = p.couriers.reduce((s, c) => s + c.pollingOffCount, 0)
      const total = p.couriers.reduce((s, c) => s + c.total, 0)
      const failed = p.couriers.reduce((s, c) => s + c.failed, 0)
      out.push({
        _type: 'parent',
        _key: p.name,
        name: p.name,
        childCount: p.couriers.length,
        totalAccounts,
        nativeAccounts,
        pollingOffCount,
        total,
        failed,
        failureRatePct: total ? (failed / total) * 100 : 0,
        sharePct: grandTotal ? (total / grandTotal) * 100 : 0,
        live: nativeAccounts > 0,
        // PCIS reports native volume per parent courier only (not per sub-courier/direction),
        // so this is attached at the parent row and left blank on children.
        nativeVol: nativeByParent.get(p.name.toLowerCase()) ?? null,
      })
      if (expanded.has(p.name)) {
        p.couriers.forEach((c) => {
          out.push({
            _type: 'child',
            _key: `${p.name}::${c.courierCode}::${c.serviceType}`,
            ...c,
            failureRatePct: c.total ? (c.failed / c.total) * 100 : 0,
            sharePct: grandTotal ? (c.total / grandTotal) * 100 : 0,
          })
        })
      }
    })
    return out
  }, [parents, expanded, grandTotal, nativeByParent])

  /* Migration runway: distinct Clickpost accounts, grouped by parent brand.
     Counted directly off the accounts catalog — summing a per-direction field
     off /health/adoption's courier rows double-counted couriers that share one
     account pool across FORWARD and RVP (e.g. Delhivery, Ekart), since each
     direction carried its own copy of the same account count. */
  const runway = useMemo(() => {
    const byParent = new Map() // normKey -> {displayName, count}
    ;(accounts || []).forEach((a) => {
      if (a.integration_type !== 'CLICKPOST_FALLBACK') return
      const raw = a.courier_parent || a.courier_name || a.courier_code || 'Unknown'
      const stripped = stripDirectionSuffix(raw) || raw
      const normKey = stripped.toLowerCase()
      const existing = byParent.get(normKey)
      const displayName = existing ? (raw.toLowerCase() === normKey ? raw : existing.displayName) : stripped
      byParent.set(normKey, {displayName, count: (existing?.count || 0) + 1})
    })
    return Array.from(byParent.values(), ({displayName, count}) => ({name: displayName, count})).sort((a, b) => b.count - a.count)
  }, [accounts])
  const runwayMax = runway[0]?.count || 1
  const clickpostAccountsTotal = runway.reduce((s, r) => s + r.count, 0)

  const loading = adoptionFetching || !accounts

  if (isError) return <LoadErrorPanel message={error?.message || 'Could not load courier data.'} onRetry={refetch} />
  if (loading && !adoption) return <TableSkeleton kpis={3} rows={8} />
  if (!adoption || !accounts) return <Empty />

  const totalAccounts = accounts.length
  const nativeAccountsAll = accounts.filter((a) => a.integration_type === 'NATIVE')
  const nativeAccountsCount = nativeAccountsAll.length
  const clickpostAccountsCount = totalAccounts - nativeAccountsCount
  const pollingEnabledTotal = nativeAccountsAll.filter((a) => a.polling_enabled).length

  const totalPartners = parents.length
  const nativePartners = parents.filter((p) => p.couriers.some((c) => c.nativeAccounts > 0)).length
  const partnersWithVolume = parents.filter((p) => p.couriers.some((c) => c.total > 0)).length

  const columns = [
    {
      key: 'courierCode',
      header: 'Courier',
      render: (r) =>
        r._type === 'parent' ? (
          <Stack direction='row' alignItems='center' spacing={0.75}>
            <Typography sx={{fontSize: 12, color: 'text.secondary', width: 12, textAlign: 'center'}}>
              {expanded.has(r.name) ? '▾' : '▸'}
            </Typography>
            <Box>
              <Typography sx={{fontSize: 12.5, fontWeight: 700, lineHeight: 1.25}}>{r.name}</Typography>
              <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>
                {r.childCount} sub-courier{r.childCount === 1 ? '' : 's'}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Box sx={{pl: 2.75}}>
            <Typography sx={{fontSize: 12.5, fontWeight: 700, lineHeight: 1.25}}>{r.courierCode}</Typography>
            <Typography sx={{fontSize: 10.5, color: 'text.secondary', lineHeight: 1.2}}>
              {[r.courierName, r.serviceType].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) =>
        !r.live ? (
          <Pill palette={SLATE} title='No account here is set to NATIVE'>
            Not live
          </Pill>
        ) : r.total > 0 ? (
          <Pill palette={TEAL}>Native</Pill>
        ) : (
          <Pill palette={AMBER} title='Configured NATIVE but placed no orders in this window'>
            Idle
          </Pill>
        ),
    },
    {
      key: 'accounts',
      header: 'Accounts',
      align: 'right',
      render: (r) => (
        <Box>
          <Typography sx={{fontSize: 12, fontWeight: 700}}>
            {nf(r.nativeAccounts)}
            <Box component='span' sx={{color: 'text.disabled', fontWeight: 500}}> / {nf(r.totalAccounts)}</Box>
          </Typography>
          <Typography sx={{fontSize: 10, color: 'text.secondary'}}>native / total</Typography>
        </Box>
      ),
    },
    {
      key: 'nativeVol',
      header: (
        <HeaderTip
          label='Booked shipment'
          title={`Successful Direct Integration placements, as PCIS recorded them at booking time. This figure covers BOTH directions and does not change with the ${DIR_LABEL[direction]} tab — PCIS reports native volume per parent courier only, with no direction breakdown. Total trials beside it IS direction-scoped, so the two deliberately count different populations.`}
        />
      ),
      align: 'right',
      render: (r) =>
        r._type !== 'parent' ? (
          <Tooltip title="PCIS reports native volume per parent courier only — expand for this sub-courier's account-level status instead.">
            <Box sx={{color: 'text.disabled'}}>—</Box>
          </Tooltip>
        ) : !r.nativeVol ? (
          <Box sx={{color: 'text.disabled'}}>—</Box>
        ) : (
          <Box>
            <Typography sx={{fontSize: 12, fontWeight: 700, color: TEAL.main}}>{nf(r.nativeVol)}</Typography>
            {/* "both directions", not "booked". The value is fleet-wide — PCIS's native-volume
                report takes only a date range, no direction — so it is identical on the Forward
                and Reverse tabs. Left unlabelled it read as that direction's volume, which
                double-counted every courier and produced visible nonsense: Ekart on Reverse
                showing 731 booked next to 0 trials and an Idle badge. */}
            <Typography sx={{fontSize: 10, color: 'text.secondary'}}>both directions</Typography>
          </Box>
        ),
    },
    {
      key: 'polling',
      header: 'Polling',
      align: 'right',
      render: (r) =>
        r.nativeAccounts === 0 ? (
          <Box sx={{color: 'text.disabled'}}>—</Box>
        ) : (
          <Pill
            palette={r.pollingOffCount === 0 ? TEAL : r.pollingOffCount === r.nativeAccounts ? CORAL : AMBER}
            title={`${nf(r.pollingOffCount)} native account(s) with polling off`}
          >
            {nf(r.nativeAccounts - r.pollingOffCount)}/{nf(r.nativeAccounts)}
          </Pill>
        ),
    },
    {
      key: 'total',
      header: (
        <HeaderTip
          label='Total trials'
          title="Placement attempts (success + failure) PCIS tracked for this courier — not the same population as Booked shipment, which counts successful placements only."
        />
      ),
      align: 'right',
      render: (r) => <Typography sx={{fontSize: 12.5, fontWeight: 700}}>{nf(r.total)}</Typography>,
    },
    {
      key: 'share',
      header: (
        <HeaderTip
          label='Share'
          title="This courier's share of the Orders column across all couriers — a share of PCIS-tracked attempts, not of total fleet volume including Clickpost."
        />
      ),
      width: 130,
      render: (r) => (
        <Stack direction='row' alignItems='center' spacing={1}>
          <Meter value={r.sharePct} palette={INDIGO} sx={{flex: 1}} />
          <Typography sx={{fontSize: 11, color: 'text.secondary', width: 38, textAlign: 'right'}}>{pct(r.sharePct)}</Typography>
        </Stack>
      ),
    },
    {
      key: 'failureRatePct',
      header: 'Failure',
      align: 'right',
      render: (r) => (r.total ? <Pill palette={rateBand(r.failureRatePct).c}>{pct(r.failureRatePct)}</Pill> : <Box sx={{color: 'text.disabled'}}>—</Box>),
    },
    {
      key: 'p95',
      header: 'p50 / p95',
      align: 'right',
      render: (r) =>
        r._type === 'parent' ? (
          <Tooltip title='Expand for per-courier latency'>
            <Box sx={{color: 'text.disabled'}}>—</Box>
          </Tooltip>
        ) : r.p95Ms == null ? (
          <Box sx={{color: 'text.disabled'}}>—</Box>
        ) : (
          <Typography sx={{fontSize: 12}}>
            <Box component='span' sx={{color: 'text.secondary'}}>{ms(r.p50Ms)}</Box>
            {' / '}
            <Box component='span' sx={{fontWeight: 700, color: r.p95Ms >= 3000 ? CORAL.main : 'text.primary'}}>{ms(r.p95Ms)}</Box>
          </Typography>
        ),
    },
  ]

  return (
    <>
      <StatGrid>
        <Stat
          label='Courier partners'
          value={nf(totalPartners)}
          palette={INDIGO}
          sub={`${nf(nativePartners)} configured native · ${nf(partnersWithVolume)} placing orders`}
        />
        <Stat
          label='Accounts'
          value={nf(totalAccounts)}
          palette={VIOLET}
          share={totalAccounts ? (nativeAccountsCount / totalAccounts) * 100 : 0}
          sub={`${nf(nativeAccountsCount)} native · ${nf(clickpostAccountsCount)} Clickpost`}
        />
        <Stat
          label='Polling enabled'
          value={nf(pollingEnabledTotal)}
          unit={`/ ${nf(nativeAccountsCount)}`}
          palette={TEAL}
          share={nativeAccountsCount ? (pollingEnabledTotal / nativeAccountsCount) * 100 : 0}
          sub='Native accounts tracking scans'
        />
      </StatGrid>

      <Panel
        title='Courier scorecard'
        note={
          railDirection
            ? `${DIR_LABEL[direction]} only — set by the Delivery Type filter above. Click a parent row to see its sub-couriers.`
            : `${DIR_LABEL[direction]} couriers — click a parent row to see its sub-couriers. Every configured partner in this direction appears, whether or not it placed orders in this window.`
        }
        action={
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <DirectionTabs value={direction} onChange={setDirTab} locked={!!railDirection} />
            <ColorLegend />
          </Stack>
        }
        sx={{mt: 2}}
      >
        {/* Only parent rows are clickable — expanding shows/hides that parent's
            sub-courier rows in place. This no longer touches the page-wide
            courier filter; that lives only in the Courier dropdown above. */}
        <Box
          sx={{'& tbody tr': {cursor: 'default'}}}
          onClick={(e) => {
            const row = e.target.closest('tr')
            const key = row?.querySelector('[data-parent]')?.dataset?.parent
            if (key) toggle(key)
          }}
        >
          <MiniTable
            columns={columns.map((c, i) =>
              i === 0
                ? {
                    ...c,
                    render: (r) => (
                      <Box data-parent={r._type === 'parent' ? r.name : undefined} sx={{cursor: r._type === 'parent' ? 'pointer' : 'default'}}>
                        {c.render(r)}
                      </Box>
                    ),
                  }
                : c
            )}
            rows={displayRows}
            getKey={(r) => r._key}
            empty='No courier partners configured.'
          />
        </Box>
      </Panel>

      <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'}, gap: 2, mt: 2}}>
        <Panel title='Migration runway' note='Accounts still routing through Clickpost, by parent brand'>
          {runway.length ? (
            <>
              <Stack spacing={1.25}>
                {runway.map((r) => (
                  <LabelledBar key={r.name} label={r.name} value={nf(r.count)} share={(r.count / runwayMax) * 100} palette={AMBER} />
                ))}
              </Stack>
              <Typography sx={{fontSize: 11.5, color: 'text.secondary', mt: 2}}>
                {nf(clickpostAccountsTotal)} accounts left in total.{' '}
                {fleetRouting
                  ? `They carried ${nf(fleetRouting.clickpostOrders)} orders (${pct(fleetRouting.clickpostSharePct)}) in this window.`
                  : 'No orders in this window.'}
              </Typography>
            </>
          ) : (
            <Empty>Every account is on PCIS native.</Empty>
          )}
        </Panel>

        <Panel title='Configured vs actual' note='Switching an account to native only counts once orders follow'>
          <Stack spacing={2.25}>
            <Box>
              <Stack direction='row' justifyContent='space-between' sx={{mb: 0.75}}>
                <Typography sx={{fontSize: 12.5, fontWeight: 600}}>Accounts configured native</Typography>
                <Typography sx={{fontSize: 12.5, fontWeight: 700}}>
                  {pct(adoption.totalAccounts ? (adoption.nativeAccounts / adoption.totalAccounts) * 100 : 0)}
                </Typography>
              </Stack>
              <Meter value={adoption.totalAccounts ? (adoption.nativeAccounts / adoption.totalAccounts) * 100 : 0} palette={VIOLET} height={10} />
            </Box>
            <Box>
              <Stack direction='row' justifyContent='space-between' sx={{mb: 0.75}}>
                <Tooltip title="From Proship's own order record (tms_source at booking time), not PCIS's account config — accurate for this window even after a later native/Clickpost switch.">
                  <Typography sx={{fontSize: 12.5, fontWeight: 600, borderBottom: '1px dashed', borderColor: 'divider', width: 'fit-content'}}>
                    Volume actually placed natively
                  </Typography>
                </Tooltip>
                <Typography sx={{fontSize: 12.5, fontWeight: 700}}>
                  {fleetRouting ? pct(fleetRouting.nativeSharePct) : '—'}
                </Typography>
              </Stack>
              <Meter value={fleetRouting?.nativeSharePct || 0} palette={TEAL} height={10} />
            </Box>
            <Typography sx={{fontSize: 11.5, color: 'text.secondary', lineHeight: 1.6}}>
              The two rarely match. Configured share counts intent; volume share counts reality — a low-traffic account flipped
              to native moves the first and not the second. Where volume share leads, the migrated accounts are the busy ones.
            </Typography>
            {adoption.nativePartners > adoption.partnersWithVolume ? (
              <Typography sx={{fontSize: 11.5, color: AMBER.main, fontWeight: 600}}>
                {nf(adoption.nativePartners - adoption.partnersWithVolume)} courier(s) are configured native but placed nothing
                this window.
              </Typography>
            ) : null}
          </Stack>
        </Panel>
      </Box>
    </>
  )
}

TabCouriers.propTypes = {
  filters: PropTypes.object.isRequired,
  pollMs: PropTypes.number,
  accounts: PropTypes.array,
}
